/**
 * 지출결의 공통 워크스페이스
 * - register: 마스터 → 디테일 → 접는 목록 · 불러오기(원인행위|현액) · 전자증빙→결의초안
 * - approve: 마스터+디테일 조인 그리드 승인/반려
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, FormProvider, useWatch } from 'react-hook-form';
import styled from 'styled-components';
import { useAuth } from 'contexts/AuthContext';
import useTenantCode, { buildPrefsUserId } from 'hooks/useTenantCode';
import { PageContainer } from 'styles/common.styles';
import { theme, SEARCH_LABEL_W } from 'styles/theme';
import { hoverExpandScrollbarCss } from 'styles/scrollbar.styles';
import useScrollbarHoverProps from 'hooks/useScrollbarHoverProps';
import {
  FormArea,
  CollapsibleSearchPanel,
  CollapsibleSectionPanel,
  ActionButtonGroup,
  getCurrentFiscalYear,
} from 'components/forms';
import { HiGridComponent, useHiGridHook, hiCol } from 'components/grid/hiGrid';
import {
  GridExportProvider,
  GridExportFocusScope,
  useRegisterGridExport,
  useExcelExportAction,
} from 'components/grid/excel';
import {
  ActiveGridProvider,
  ActiveGridScope,
  ActiveGridHeader,
  useActiveGridCrudActions,
} from 'contexts/ActiveGridContext';
import { showAlert, showConfirm } from 'utils/toastBridge';
import CodeLookupDialog from 'components/budget/CodeLookupDialog';
import { loadDepts, filterDepts } from 'master/deptStore';
import { loadOrSeedProjects } from 'budget/budgetProjectStore';
import { loadSubjectsByFilter } from 'budget/budgetStructureStore';
import {
  getExpenseLoadEnv,
  buildLoadDialogTabs,
  loadExpenseBalanceRows,
  BALANCE_SEARCH_CONFIG,
  applyBalanceFields,
  confirmUpstreamDupIfNeeded,
} from 'expense/expenseLoadUtils';
import {
  EVIDENCE_SOURCE,
  ensureEvidenceSeed,
  filterOpenEvidences,
  loadEvidences,
  fetchEvidences,
  resolveAxisFromEvidences,
  isAxisComplete,
  detailFromEvidence,
  linkEvidencesToResolution,
  unlinkEvidences,
  syncEvidencesForResolution,
  releaseEvidencesByResolution,
  reconcileOrphanEvidenceLinks,
  evidenceSourceLabel,
} from 'expense/expenseEvidenceStore';
import {
  EXPENSE_STATUS,
  EXPENSE_STATUS_OPTIONS,
  RESOLUTION_TYPE_OPTIONS,
  resolutionTypeLabel,
  expenseStatusLabel,
  createResolutionUid,
  createResolutionDetailUid,
  emptyResolutionMaster,
  emptyResolutionDetailRow,
  loadResolutionBundle,
  saveResolutionBundle,
  loadObligationBundle,
  saveObligationBundle,
  ensureObligationForResolution,
  filterResolutionsForRegister,
  loadJoinedForApprove,
  requestResolutionApproval,
  approveResolution,
  rejectResolution,
  todayYmd,
} from 'expense/expenseStore';

const LIST_GRID_ID = 'expense-resolution-list';
const DETAIL_GRID_ID = 'expense-resolution-detail';
const APPROVE_GRID_ID = 'expense-resolution-approve';
/** 엑셀 내보내기용 마스터(폼) — 결의목록 그리드는 등록하지 않음 */
const MASTER_EXPORT_ID = 'expense-resolution-master';

// 영역별 높이 — 목록을 펼쳐도 서로 줄이지 않고 아래로 늘어나므로 각자 높이를 갖는다
const DETAIL_PANEL_H = '300px'; // 최소 높이(남는 공간이 있으면 더 늘어남)
const LIST_PANEL_H = '340px';

/**
 * 세로 흐름 — 각 영역은 자기 높이를 갖고, 목록을 펼치면 위 영역을 줄이지 않고
 * 아래로 늘어나며 화면이 스크롤된다 (형제 간 높이 분배 아님).
 */
const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  ${hoverExpandScrollbarCss}

  > * {
    flex-shrink: 0;
  }
`;

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: ${(p) => p.$flex || '0 0 auto'};
  height: ${(p) => p.$height || 'auto'};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  overflow: hidden;
  background: ${theme.colors.white};
`;

const GridBody = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const Hint = styled.div`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.text.secondary};
  padding: 0 2px;
`;

/** 버튼 유무와 무관하게 높이 고정 — 추가/선택 시 그리드 영역이 밀리지 않도록 */
const MasterToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  padding: 4px 8px;
  min-height: 34px;
  box-sizing: border-box;
  border-bottom: 1px solid ${theme.colors.border};
  flex-shrink: 0;
`;

const MasterToolbarActions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`;

const MasterToolbarTitle = styled.strong`
  font-size: ${theme.typography.fontSize.sm};
  flex-shrink: 0;
`;

const LoadBtn = styled.button`
  appearance: none;
  border: 1px solid ${theme.colors.border};
  background: ${theme.colors.white};
  color: ${theme.colors.text.primary};
  font-size: ${theme.typography.fontSize.xs};
  padding: 3px 8px;
  min-height: 26px;
  border-radius: ${theme.borderRadius.sm};
  cursor: pointer;
  &:hover:not(:disabled) {
    border-color: ${theme.colors.primary};
    color: ${theme.colors.primary};
  }
  &:disabled {
    background: ${theme.colors.background.light};
    color: ${theme.colors.text.secondary};
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

/**
 * 열 슬롯 폭 — 품의·원인행위등록과 동일 (시작점 정렬, 끝점 가변)
 */
const SEARCH_SLOT_W = ['230px', '280px', '330px'];
const SEARCH_DATE_W = '116px';

const MASTER_SLOT_W = ['210px', '230px', '290px', '290px', '200px', '200px'];
/** 적요는 재원+원인행위번호(짧은 2개 열) 자리를 합쳐 사용 */
const MASTER_SLOT_W_REMARK = '412px';
const MASTER_LABEL_W = '64px';

function emptySearch(partial = {}) {
  return {
    searchFiscalYear: getCurrentFiscalYear(),
    searchStatus: '',
    searchDeptCode: '',
    searchDeptName: '',
    searchDeptDisp: '',
    searchProjectCode: '',
    searchProjectName: '',
    searchProjectDisp: '',
    searchSubjectCode: '',
    searchSubjectName: '',
    searchSubjectDisp: '',
    searchDateFrom: '',
    searchDateTo: '',
    searchRemark: '',
    ...partial,
  };
}

function summarizeSelection(names = []) {
  const list = (names || []).map((s) => String(s || '').trim()).filter(Boolean);
  if (!list.length) return '';
  if (list.length === 1) return list[0];
  return `${list[0]} 외 ${list.length - 1}건`;
}

function selectionTooltip(joinedNames, emptyText) {
  const list = String(joinedNames || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  if (!list.length) return emptyText;
  if (list.length === 1) return list[0];
  return `선택 ${list.length}건\n${list.map((n) => `· ${n}`).join('\n')}`;
}

/** 조회조건 키 — 마스터 선택/reset 시 덮어쓰지 않음 */
const SEARCH_FIELD_KEYS = [
  'searchFiscalYear',
  'searchStatus',
  'searchDeptCode',
  'searchDeptName',
  'searchDeptDisp',
  'searchProjectCode',
  'searchProjectName',
  'searchProjectDisp',
  'searchSubjectCode',
  'searchSubjectName',
  'searchSubjectDisp',
  'searchDateFrom',
  'searchDateTo',
  'searchRemark',
];

function pickSearchValues(values = {}) {
  const out = {};
  SEARCH_FIELD_KEYS.forEach((k) => {
    out[k] = values[k] ?? '';
  });
  return out;
}

function emptyMasterForm(partial = {}) {
  return emptyResolutionMaster({
    resolutionDate: todayYmd(),
    ...partial,
  });
}

/**
 * @param {{ screenMode?: 'register'|'approve' }} props
 */
function ExpenseResolutionWorkspaceInner({ screenMode = 'register' }) {
  const isApprove = screenMode === 'approve';
  const { user } = useAuth();
  const tenantCode = useTenantCode();
  const prefsUserId = useMemo(() => buildPrefsUserId(tenantCode, user), [tenantCode, user]);
  const scrollbarHoverProps = useScrollbarHoverProps();

  const searchDefaults = useMemo(
    () => emptySearch({ searchStatus: isApprove ? EXPENSE_STATUS.REQUESTED : '' }),
    [isApprove]
  );

  const methods = useForm({
    mode: 'onSubmit',
    defaultValues: {
      ...searchDefaults,
      ...emptyMasterForm({ fiscalYear: searchDefaults.searchFiscalYear }),
      obligationPicker: '',
    },
  });
  const { getValues, reset, setValue, control } = methods;

  const listRef = useRef(null);
  const detailRef = useRef(null);
  const approveRef = useRef(null);
  const [selectedUid, setSelectedUid] = useState('');
  const selectedUidRef = useRef('');
  const [bundleDetails, setBundleDetails] = useState([]);
  const [obligationOptions, setObligationOptions] = useState([]);
  const [loadLookupOpen, setLoadLookupOpen] = useState(false);
  const [cardLookupOpen, setCardLookupOpen] = useState(false);
  const [axisPickOpen, setAxisPickOpen] = useState(false);
  const [searchLookup, setSearchLookup] = useState({ open: false, type: '' });
  const [listCount, setListCount] = useState(0);
  /** 전자증빙 선택 후 축 보완 대기 */
  const pendingCardEvidencesRef = useRef([]);
  const linkedEvidenceUidsRef = useRef([]);

  const masterStatus = useWatch({ control, name: 'status' });
  const searchFiscalYearWatch = useWatch({ control, name: 'searchFiscalYear' });
  const searchDeptNameWatch = useWatch({ control, name: 'searchDeptName' });
  const searchProjectNameWatch = useWatch({ control, name: 'searchProjectName' });
  const searchSubjectNameWatch = useWatch({ control, name: 'searchSubjectName' });
  const masterReadonly =
    isApprove ||
    (masterStatus &&
      masterStatus !== EXPENSE_STATUS.DRAFT &&
      masterStatus !== EXPENSE_STATUS.REJECTED);

  useEffect(() => {
    selectedUidRef.current = selectedUid;
  }, [selectedUid]);

  const { handleActiveAdd, handleActiveDelete, activeLabel, activeGridId } = useActiveGridCrudActions({
    guardAdd: (id) => {
      if (isApprove) return '승인 화면에서는 추가할 수 없습니다.';
      if (id === DETAIL_GRID_ID && !selectedUidRef.current) {
        return '결의를 선택하거나 신규 추가하세요.';
      }
      return null;
    },
    guardDelete: () => (isApprove ? '승인 화면에서는 삭제할 수 없습니다.' : null),
  });

  const dimsFromSearch = useCallback(() => {
    const v = getValues();
    return {
      tenantCode,
      fiscalYear: String(v.searchFiscalYear || '').trim(),
    };
  }, [getValues, tenantCode]);

  const buildSearchFilter = useCallback(() => {
    const v = getValues();
    return {
      status: String(v.searchStatus || '').trim() || undefined,
      deptCode: String(v.searchDeptCode || '').trim() || undefined,
      deptName: String(v.searchDeptName || '').trim() || undefined,
      projectCode: String(v.searchProjectCode || '').trim() || undefined,
      projectName: String(v.searchProjectName || '').trim() || undefined,
      subjectCode: String(v.searchSubjectCode || '').trim() || undefined,
      subjectName: String(v.searchSubjectName || '').trim() || undefined,
      dateFrom: String(v.searchDateFrom || '').trim() || undefined,
      dateTo: String(v.searchDateTo || '').trim() || undefined,
      remark: String(v.searchRemark || '').trim() || undefined,
    };
  }, [getValues]);

  const enrichListRow = useCallback(
    (row) => ({
      ...row,
      statusNm: expenseStatusLabel(row.status),
      resolutionTypeNm: resolutionTypeLabel(row.resolutionType),
    }),
    []
  );

  const listColumns = useMemo(
    () => [
      hiCol.roTextColumn({ header: '결의번호', field: 'resolutionNo', size: 90, align: 'center' }),
      hiCol.roTextColumn({ header: '원인행위', field: 'obligationNo', size: 80, align: 'center' }),
      hiCol.roTextColumn({ header: '부서', field: 'deptName', size: 90 }),
      hiCol.roNumberColumn({ header: '결의금액', field: 'resolutionAmt', size: 100, align: 'right' }),
      hiCol.roTextColumn({ header: '상태', field: 'statusNm', size: 80, align: 'center' }),
    ],
    []
  );

  const detailColumns = useMemo(
    () => [
      hiCol.edTextColumn({ header: '거래처코드', field: 'vendorCode', size: 90, editable: !masterReadonly }),
      hiCol.edTextColumn({ header: '거래처명', field: 'vendorName', size: 120, editable: !masterReadonly }),
      hiCol.edTextColumn({ header: '증빙유형', field: 'evidenceType', size: 80, editable: !masterReadonly }),
      hiCol.edTextColumn({ header: '증빙번호', field: 'evidenceNo', size: 100, editable: !masterReadonly }),
      hiCol.edComboColumn({
        header: '부가세',
        field: 'vatYn',
        size: 70,
        options: [
          { value: 'Y', label: 'Y' },
          { value: 'N', label: 'N' },
        ],
        editable: !masterReadonly,
      }),
      hiCol.edNumberColumn({ header: '부가세액', field: 'vatAmt', size: 90, align: 'right', editable: !masterReadonly }),
      hiCol.edTextColumn({ header: '차변', field: 'accountDr', size: 90, editable: !masterReadonly }),
      hiCol.edTextColumn({ header: '대변', field: 'accountCr', size: 90, editable: !masterReadonly }),
      hiCol.edTextColumn({ header: '은행', field: 'bankName', size: 90, editable: !masterReadonly }),
      hiCol.edTextColumn({ header: '계좌', field: 'accountNo', size: 110, editable: !masterReadonly }),
      hiCol.edTextColumn({ header: '카드', field: 'cardNo', size: 100, editable: !masterReadonly }),
      hiCol.edNumberColumn({ header: '금액', field: 'amount', size: 100, align: 'right', editable: !masterReadonly }),
      hiCol.edTextColumn({ header: '비고', field: 'remark', size: 120, editable: !masterReadonly }),
    ],
    [masterReadonly]
  );

  const approveColumns = useMemo(
    () => [
      hiCol.roTextColumn({ header: '결의번호', field: 'resolutionNo', size: 90, align: 'center' }),
      hiCol.roTextColumn({ header: '원인행위', field: 'obligationNo', size: 80, align: 'center' }),
      hiCol.roTextColumn({ header: '부서', field: 'deptName', size: 90 }),
      hiCol.roTextColumn({ header: '거래처', field: 'vendorName', size: 110 }),
      hiCol.roTextColumn({ header: '증빙', field: 'evidenceNo', size: 100 }),
      hiCol.roNumberColumn({ header: '금액', field: 'amount', size: 100, align: 'right' }),
      hiCol.roTextColumn({ header: '상태', field: 'statusNm', size: 80, align: 'center' }),
    ],
    []
  );

  const detailGrid = useHiGridHook({
    gridRef: detailRef,
    autoSearch: false,
    enableContextMenu: !isApprove && !masterReadonly,
    confirmCheck: !isApprove && !masterReadonly,
    defaultRow: emptyResolutionDetailRow(),
  });

  const listGrid = useHiGridHook({
    gridRef: listRef,
    autoSearch: false,
    enableContextMenu: false,
    confirmCheck: false,
  });

  const approveGrid = useHiGridHook({
    gridRef: approveRef,
    autoSearch: false,
    enableContextMenu: false,
    confirmCheck: false,
  });

  const loadObligationPicker = useCallback(async () => {
    const dims = dimsFromSearch();
    const bundle = await loadObligationBundle(dims);
    const opts = (bundle.masters || [])
      .filter((m) => m.mod !== 'D')
      .map((m) => ({
        value: m.obligationUid,
        label: `${m.obligationNo} · ${m.deptName || ''} · ${Number(m.obligationAmt || 0).toLocaleString()}원`,
        ...m,
      }));
    setObligationOptions(opts);
  }, [dimsFromSearch]);

  const loadDetails = useCallback(
    (uid) => {
      const list = (bundleDetails || []).filter((d) => d.resolutionUid === uid && d.mod !== 'D');
      detailRef.current?.setData?.(list);
      // 저장된 디테일 기준 점유 증빙 추적 (신규 선택 시 이전 해제용)
      linkedEvidenceUidsRef.current = list.map((d) => d.evidenceUid).filter(Boolean);
    },
    [bundleDetails]
  );

  const loadMasterToForm = useCallback(
    (row) => {
      const searchKeep = pickSearchValues(getValues());
      if (!row) {
        reset({
          ...searchKeep,
          ...emptyMasterForm({ fiscalYear: searchKeep.searchFiscalYear }),
          obligationPicker: '',
        });
        detailRef.current?.setData?.([]);
        return;
      }
      reset({
        ...searchKeep,
        ...row,
        statusNm: expenseStatusLabel(row.status),
        obligationPicker: row.obligationUid || '',
      });
      loadDetails(row.resolutionUid);
    },
    [reset, getValues, loadDetails]
  );

  const handleSearch = useCallback(async () => {
    const dims = dimsFromSearch();
    if (!dims.fiscalYear) {
      showAlert('회계연도를 입력하세요.');
      return;
    }
    await loadObligationPicker();
    const filter = buildSearchFilter();

    if (isApprove) {
      const rows = await loadJoinedForApprove(dims);
      // 조인 행에도 마스터 축·결의일·적요가 있으므로 동일 필터 적용
      const filtered = filterResolutionsForRegister(rows, {
        ...filter,
        // 승인화면 기본: 상태 미지정이면 승인요청만 (loadJoined가 이미 REQUESTED만 줌)
        status: filter.status,
      });
      approveRef.current?.setData?.(filtered);
      return;
    }

    const bundle = await loadResolutionBundle(dims);
    // 삭제·미존재 결의를 가리키는 증빙 고아 연결 정리
    ensureEvidenceSeed(tenantCode, dims.fiscalYear);
    await fetchEvidences(tenantCode, dims.fiscalYear);
    const aliveUids = (bundle.masters || [])
      .filter((m) => m.mod !== 'D')
      .map((m) => m.resolutionUid)
      .filter(Boolean);
    reconcileOrphanEvidenceLinks(tenantCode, dims.fiscalYear, aliveUids);

    let masters = filterResolutionsForRegister(bundle.masters, filter);
    masters = masters.map(enrichListRow);
    setBundleDetails(bundle.details);
    listRef.current?.setData?.(masters);
    setListCount(masters.length);
    const first = masters[0];
    setSelectedUid(first?.resolutionUid || '');
    loadMasterToForm(first);
  }, [
    dimsFromSearch,
    buildSearchFilter,
    isApprove,
    enrichListRow,
    loadMasterToForm,
    loadObligationPicker,
    tenantCode,
  ]);

  useEffect(() => {
    reset({
      ...searchDefaults,
      ...emptyMasterForm({ fiscalYear: searchDefaults.searchFiscalYear }),
      obligationPicker: '',
    });
  }, [reset, searchDefaults]);

  const onListSelect = useCallback(
    (row) => {
      setSelectedUid(row?.resolutionUid || '');
      loadMasterToForm(row);
    },
    [loadMasterToForm]
  );

  useEffect(() => {
    listGrid.onRowClicked = ({ data }) => onListSelect(data);
    listGrid.onSelectRow = ({ data }) => onListSelect(data);
  }, [listGrid, onListSelect]);

  useEffect(() => {
    approveGrid.onRowClicked = ({ data }) => {
      selectedUidRef.current = data?.resolutionUid || '';
      setSelectedUid(data?.resolutionUid || '');
    };
    approveGrid.onSelectRow = ({ data }) => {
      selectedUidRef.current = data?.resolutionUid || '';
      setSelectedUid(data?.resolutionUid || '');
    };
  }, [approveGrid]);

  const collectDetails = useCallback(() => {
    const uid = selectedUidRef.current;
    return (detailRef.current?.getData?.() || [])
      .filter((r) => r.mod !== 'D')
      .map((r) => ({
        ...r,
        resolutionUid: uid,
        detailUid: r.detailUid || createResolutionDetailUid(),
      }));
  }, []);

  const handleNew = useCallback(() => {
    if (isApprove) return;
    // 미저장 전자증빙 초안 연결 해제
    const prevLinked = linkedEvidenceUidsRef.current || [];
    if (prevLinked.length) {
      const dims = dimsFromSearch();
      unlinkEvidences(tenantCode, dims.fiscalYear, prevLinked);
      linkedEvidenceUidsRef.current = [];
    }
    const uid = createResolutionUid();
    const row = enrichListRow(
      emptyResolutionMaster({
        resolutionUid: uid,
        fiscalYear: dimsFromSearch().fiscalYear,
        resolutionDate: todayYmd(),
        status: EXPENSE_STATUS.DRAFT,
        registerDate: todayYmd(),
        mod: 'A',
      })
    );
    setSelectedUid(uid);
    reset({ ...pickSearchValues(getValues()), ...row, obligationPicker: '' });
    detailRef.current?.setData?.([]);
  }, [isApprove, enrichListRow, dimsFromSearch, reset, getValues, tenantCode]);

  /** 전자증빙 선택 → 마스터+디테일 초안 (축은 인자로 확정된 상태) */
  const applyEvidenceDraft = useCallback(
    (evidences, axis) => {
      const list = (Array.isArray(evidences) ? evidences : [evidences]).filter(Boolean);
      if (!list.length) return;

      const dims = dimsFromSearch();
      const uid = createResolutionUid();
      const total = list.reduce((s, e) => s + (Number(e.amount) || 0), 0);
      const dates = list.map((e) => e.useDate).filter(Boolean).sort();
      const merchants = [...new Set(list.map((e) => e.merchantName).filter(Boolean))];
      const remark =
        merchants.length <= 2
          ? merchants.join(', ')
          : `${merchants[0]} 외 ${merchants.length - 1}건`;
      const sourceNm = evidenceSourceLabel(list[0]?.source) || '전자증빙';

      const master = enrichListRow(
        emptyResolutionMaster({
          resolutionUid: uid,
          fiscalYear: dims.fiscalYear,
          resolutionDate: dates[dates.length - 1] || todayYmd(),
          registerDate: todayYmd(),
          status: EXPENSE_STATUS.DRAFT,
          resolutionAmt: total,
          remark: remark || `${sourceNm} 결의`,
          balanceUid: axis.balanceUid || '',
          deptCode: axis.deptCode || '',
          deptName: axis.deptName || '',
          projectCode: axis.projectCode || '',
          projectName: axis.projectName || '',
          subjectCode: axis.subjectCode || '',
          subjectName: axis.subjectName || '',
          fundCode: axis.fundCode || '',
          fundName: axis.fundName || '',
          mod: 'A',
        })
      );

      // 이전 미저장 연결 해제 후 새 연결
      const prevLinked = linkedEvidenceUidsRef.current || [];
      if (prevLinked.length) {
        unlinkEvidences(tenantCode, dims.fiscalYear, prevLinked);
      }
      const evUids = list.map((e) => e.evidenceUid).filter(Boolean);
      linkEvidencesToResolution(tenantCode, dims.fiscalYear, evUids, uid);
      linkedEvidenceUidsRef.current = evUids;

      setSelectedUid(uid);
      selectedUidRef.current = uid;
      reset({
        ...pickSearchValues(getValues()),
        ...master,
        obligationPicker: '',
        statusNm: expenseStatusLabel(master.status),
      });

      const details = list.map((e) => ({
        ...detailFromEvidence(e, uid),
        detailUid: createResolutionDetailUid(),
      }));
      detailRef.current?.setData?.(details);
      showAlert(
        `${sourceNm} ${list.length}건 → 결의 초안 생성 (${total.toLocaleString()}원).\n저장(F10)하세요.`,
        'success'
      );
    },
    [dimsFromSearch, enrichListRow, getValues, reset, tenantCode]
  );

  const openEvidenceLookup = useCallback(() => {
    if (isApprove || masterReadonly) return;
    const dims = dimsFromSearch();
    if (!dims.fiscalYear) {
      showAlert('회계연도를 입력하세요.');
      return;
    }
    ensureEvidenceSeed(tenantCode, dims.fiscalYear);
    setCardLookupOpen(true);
  }, [isApprove, masterReadonly, dimsFromSearch, tenantCode]);

  const evidenceLookupConfig = useMemo(() => {
    if (!cardLookupOpen) {
      return { tabs: [], defaultTabId: 'card', title: '전자증빙 (미결)' };
    }
    const dims = dimsFromSearch();
    ensureEvidenceSeed(tenantCode, dims.fiscalYear);
    const all = loadEvidences(tenantCode, dims.fiscalYear);
    const mapRow = (r) => ({
      ...r,
      id: r.evidenceUid,
      amountDisp: Number(r.amount) || 0,
    });
    const cardRows = filterOpenEvidences(all, { source: EVIDENCE_SOURCE.CARD }).map(mapRow);
    const taxRows = filterOpenEvidences(all, { source: EVIDENCE_SOURCE.TAX }).map(mapRow);

    const cardColumns = [
      hiCol.roTextColumn({ header: '사용일', field: 'useDate', size: 100, align: 'center' }),
      hiCol.roTextColumn({ header: '카드', field: 'cardNo', size: 130 }),
      hiCol.roTextColumn({ header: '가맹점', field: 'merchantName', size: 140 }),
      hiCol.roNumberColumn({ header: '금액', field: 'amount', size: 100, align: 'right' }),
      hiCol.roNumberColumn({ header: '부가세', field: 'vatAmt', size: 90, align: 'right' }),
      hiCol.roTextColumn({ header: '승인번호', field: 'approveNo', size: 110 }),
      hiCol.roTextColumn({ header: '비고', field: 'remark', size: 120 }),
    ];
    const taxColumns = [
      hiCol.roTextColumn({ header: '작성일', field: 'useDate', size: 100, align: 'center' }),
      hiCol.roTextColumn({ header: '공급자', field: 'merchantName', size: 150 }),
      hiCol.roTextColumn({ header: '사업자번호', field: 'businessNo', size: 120 }),
      hiCol.roNumberColumn({ header: '금액', field: 'amount', size: 100, align: 'right' }),
      hiCol.roNumberColumn({ header: '부가세', field: 'vatAmt', size: 90, align: 'right' }),
      hiCol.roTextColumn({ header: '승인번호', field: 'approveNo', size: 160 }),
      hiCol.roTextColumn({ header: '비고', field: 'remark', size: 120 }),
    ];

    return {
      title: '전자증빙 (미결) — 선택 후 결의 초안',
      defaultTabId: 'card',
      tabs: [
        {
          id: 'card',
          label: `법인카드(${cardRows.length})`,
          hint: '미결 법인카드 사용내역. 선택 후 결의 초안(디테일)을 만듭니다.',
          columns: cardColumns,
          rows: cardRows,
        },
        {
          id: 'tax',
          label: `세금계산서(${taxRows.length})`,
          hint: '미결 매입 세금계산서. 선택 후 결의 초안(디테일)을 만듭니다.',
          columns: taxColumns,
          rows: taxRows,
        },
      ],
      enableSearch: true,
      searchConfig: {
        mode: 'unified',
        keywordFields: [
          'merchantName',
          'cardNo',
          'businessNo',
          'approveNo',
          'remark',
          'useDate',
        ],
      },
    };
  }, [cardLookupOpen, dimsFromSearch, tenantCode]);

  const handleEvidenceSelect = useCallback(
    async (payload) => {
      const rows = (Array.isArray(payload) ? payload : [payload]).filter(Boolean);
      if (!rows.length) {
        showAlert('전자증빙을 선택하세요.');
        return false;
      }
      const { axis, complete, reason } = resolveAxisFromEvidences(tenantCode, rows);
      if (complete && isAxisComplete(axis)) {
        applyEvidenceDraft(rows, axis);
        return true;
      }
      pendingCardEvidencesRef.current = rows;
      setAxisPickOpen(true);
      showAlert(
        `${reason || '예산축 미확정'}.\n현액에서 부서·사업·과목·재원을 1회 지정하면 선택 ${rows.length}건에 적용됩니다.`
      );
      return true;
    },
    [tenantCode, applyEvidenceDraft]
  );

  const axisPickConfig = useMemo(() => {
    const dims = dimsFromSearch();
    const balanceRows = loadExpenseBalanceRows(tenantCode, dims.fiscalYear);
    return {
      title: '전자증빙 결의 — 예산축 지정 (1회)',
      rows: balanceRows,
      columns: [
        hiCol.roTextColumn({ header: '부서', field: 'deptName', size: 100 }),
        hiCol.roTextColumn({ header: '사업', field: 'projectName', size: 120 }),
        hiCol.roTextColumn({ header: '과목', field: 'subjectName', size: 120 }),
        hiCol.roTextColumn({ header: '재원', field: 'fundName', size: 90 }),
        hiCol.roNumberColumn({ header: '현액', field: 'balanceAmt', size: 100, align: 'right' }),
      ],
      enableSearch: true,
      searchConfig: BALANCE_SEARCH_CONFIG,
    };
  }, [dimsFromSearch, tenantCode]);

  const handleAxisPickSelect = useCallback(
    async (row) => {
      const r = Array.isArray(row) ? row[0] : row;
      if (!r) return false;
      const pending = pendingCardEvidencesRef.current || [];
      if (!pending.length) {
        showAlert('선택된 전자증빙이 없습니다. 전자증빙을 다시 불러오세요.');
        return false;
      }
      const axis = {
        balanceUid: r.balanceUid || '',
        deptCode: r.deptCode || '',
        deptName: r.deptName || '',
        projectCode: r.projectCode || '',
        projectName: r.projectName || '',
        subjectCode: r.subjectDispCode || r.subjectCode || '',
        subjectName: r.subjectName || '',
        fundCode: r.fundCode || '',
        fundName: r.fundName || '',
      };
      if (!isAxisComplete(axis)) {
        showAlert('부서·사업·과목이 있는 현액을 선택하세요.');
        return false;
      }
      applyEvidenceDraft(pending, axis);
      pendingCardEvidencesRef.current = [];
      setAxisPickOpen(false);
      return true;
    },
    [applyEvidenceDraft]
  );

  const handleSave = useCallback(async () => {
    if (isApprove) {
      showAlert('승인 화면에서는 저장할 수 없습니다.');
      return;
    }
    const dims = dimsFromSearch();
    let uid = selectedUidRef.current;
    if (!uid) {
      uid = createResolutionUid();
      setSelectedUid(uid);
      selectedUidRef.current = uid;
    }

    const form = getValues();
    const resBundle = await loadResolutionBundle(dims);
    const oblBundle = await loadObligationBundle(dims);

    // 원인행위 선택 반영
    let masterDraft = {
      ...emptyResolutionMaster(),
      ...form,
      resolutionUid: uid,
      fiscalYear: dims.fiscalYear,
      registerDate: form.registerDate || todayYmd(),
      status: form.status || EXPENSE_STATUS.DRAFT,
      mod: 'U',
    };
    const picker = String(form.obligationPicker || '').trim();
    if (picker) {
      const obl = obligationOptions.find((o) => o.value === picker) || oblBundle.masters.find((m) => m.obligationUid === picker);
      if (obl) {
        masterDraft = {
          ...masterDraft,
          obligationUid: obl.obligationUid,
          obligationNo: obl.obligationNo,
          deptCode: obl.deptCode,
          deptName: obl.deptName,
          projectCode: obl.projectCode,
          projectName: obl.projectName,
          subjectCode: obl.subjectCode,
          subjectName: obl.subjectName,
          fundCode: obl.fundCode,
          fundName: obl.fundName,
          balanceUid: obl.balanceUid,
        };
      }
    }

    const ensured = ensureObligationForResolution(dims, masterDraft, oblBundle, resBundle);
    masterDraft = ensured.resolutionMaster;
    if (ensured.obligationBundle) {
      await saveObligationBundle(
        dims,
        ensured.obligationBundle.masters,
        ensured.obligationBundle.vendors,
        ensured.obligationBundle.contracts
      );
    }

    let masters = [...(resBundle.masters || [])];
    const idx = masters.findIndex((m) => m.resolutionUid === uid);
    if (idx >= 0) masters[idx] = { ...masters[idx], ...masterDraft, mod: 'U' };
    else masters.push({ ...masterDraft, mod: 'A' });

    let details = (resBundle.details || []).filter((d) => d.resolutionUid !== uid);
    const currentDetails = collectDetails();
    details = [...details, ...currentDetails];

    const saved = await saveResolutionBundle(dims, masters, details);

    // 디테일 evidenceUid 기준으로 전자증빙 ↔ 결의 연결 재동기화 (+ 결의번호)
    const savedMaster = saved.masters.find((m) => m.resolutionUid === uid);
    const evUids = currentDetails.map((d) => d.evidenceUid).filter(Boolean);
    syncEvidencesForResolution(tenantCode, dims.fiscalYear, {
      resolutionUid: uid,
      resolutionNo: savedMaster?.resolutionNo || masterDraft.resolutionNo || '',
      evidenceUids: evUids,
    });
    linkedEvidenceUidsRef.current = evUids;

    setBundleDetails(saved.details);
    listRef.current?.setData?.(saved.masters.filter((m) => m.mod !== 'D').map(enrichListRow));
    loadMasterToForm(saved.masters.find((m) => m.resolutionUid === uid));
    showAlert('저장했습니다.', 'success');
  }, [
    isApprove,
    dimsFromSearch,
    getValues,
    obligationOptions,
    collectDetails,
    enrichListRow,
    loadMasterToForm,
    tenantCode,
  ]);

  /** 작성중·반려 결의 soft-delete + 전자증빙 연결 해제(삭제·반납 싱크) */
  const handleDeleteResolution = useCallback(async () => {
    if (isApprove) return;
    const uid = selectedUidRef.current;
    if (!uid) {
      showAlert('삭제할 결의를 선택하세요.');
      return;
    }
    const status = getValues('status');
    if (status && status !== EXPENSE_STATUS.DRAFT && status !== EXPENSE_STATUS.REJECTED) {
      showAlert('작성중·반려 건만 삭제할 수 있습니다.');
      return;
    }
    const no = getValues('resolutionNo') || uid;
    const ok = await showConfirm(
      `결의 ${no} 을(를) 삭제할까요?\n연결된 전자증빙은 미결로 복귀합니다.`
    );
    if (!ok) return;

    const dims = dimsFromSearch();
    const bundle = await loadResolutionBundle(dims);
    const masters = (bundle.masters || []).map((m) =>
      m.resolutionUid === uid ? { ...m, mod: 'D' } : m
    );
    const details = (bundle.details || []).map((d) =>
      d.resolutionUid === uid ? { ...d, mod: 'D' } : d
    );
    await saveResolutionBundle(dims, masters, details);
    releaseEvidencesByResolution(tenantCode, dims.fiscalYear, uid);
    linkedEvidenceUidsRef.current = [];
    await handleSearch();
    showAlert('결의를 삭제했습니다. 전자증빙 연결을 해제했습니다.', 'success');
  }, [isApprove, getValues, dimsFromSearch, tenantCode, handleSearch]);

  const handleRequest = useCallback(async () => {
    if (isApprove) return;
    const uid = selectedUidRef.current;
    if (!uid) {
      showAlert('승인요청할 결의를 선택하세요.');
      return;
    }
    const ok = await showConfirm('승인요청할까요?');
    if (!ok) return;
    await handleSave();
    const dims = dimsFromSearch();
    const bundle = await loadResolutionBundle(dims);
    const result = requestResolutionApproval(bundle.masters, bundle.details, uid);
    await saveResolutionBundle(dims, result.masters, result.details);
    await handleSearch();
    showAlert('승인요청했습니다.', 'success');
  }, [isApprove, handleSave, dimsFromSearch, handleSearch]);

  const handleApprove = useCallback(async () => {
    if (!isApprove) return;
    const rows = approveRef.current?.getData?.() || [];
    const checked = approveRef.current?.getCheckedData?.() || [];
    const target = checked[0] || rows.find((r) => r.resolutionUid === selectedUidRef.current);
    if (!target?.resolutionUid) {
      showAlert('승인할 결의를 선택하세요.');
      return;
    }
    const ok = await showConfirm('승인할까요?');
    if (!ok) return;
    const dims = dimsFromSearch();
    const bundle = await loadResolutionBundle(dims);
    const result = approveResolution(bundle.masters, bundle.details, target.resolutionUid);
    await saveResolutionBundle(dims, result.masters, result.details);
    await handleSearch();
    showAlert('승인했습니다.', 'success');
  }, [isApprove, dimsFromSearch, handleSearch]);

  const handleReject = useCallback(async () => {
    if (!isApprove) return;
    const rows = approveRef.current?.getData?.() || [];
    const checked = approveRef.current?.getCheckedData?.() || [];
    const target = checked[0] || rows.find((r) => r.resolutionUid === selectedUidRef.current);
    if (!target?.resolutionUid) {
      showAlert('반려할 결의를 선택하세요.');
      return;
    }
    const ok = await showConfirm('반려할까요?');
    if (!ok) return;
    const dims = dimsFromSearch();
    const bundle = await loadResolutionBundle(dims);
    const result = rejectResolution(bundle.masters, bundle.details, target.resolutionUid);
    await saveResolutionBundle(dims, result.masters, result.details);
    await handleSearch();
    showAlert('반려했습니다.', 'success');
  }, [isApprove, dimsFromSearch, handleSearch]);

  const dispatchDetailAdd = useCallback(async () => {
    if (isApprove || masterReadonly || !selectedUidRef.current) return;
    detailRef.current?.setData?.([
      ...(detailRef.current?.getData?.() || []),
      emptyResolutionDetailRow({
        detailUid: createResolutionDetailUid(),
        resolutionUid: selectedUidRef.current,
        mod: 'A',
      }),
    ]);
  }, [isApprove, masterReadonly]);

  const applyObligationToForm = useCallback(
    (obl) => {
      if (!obl) return;
      setValue('obligationUid', obl.obligationUid || '');
      setValue('obligationNo', obl.obligationNo || '');
      setValue('obligationPicker', obl.obligationUid || '');
      setValue('balanceUid', obl.balanceUid || '');
      setValue('deptCode', obl.deptCode || '');
      setValue('deptName', obl.deptName || '');
      setValue('projectCode', obl.projectCode || '');
      setValue('projectName', obl.projectName || '');
      setValue('subjectCode', obl.subjectCode || '');
      setValue('subjectName', obl.subjectName || '');
      setValue('fundCode', obl.fundCode || '');
      setValue('fundName', obl.fundName || '');
      if (obl.obligationAmt != null && !getValues('resolutionAmt')) {
        setValue('resolutionAmt', obl.obligationAmt);
      }
    },
    [setValue, getValues]
  );

  const loadDialogConfig = useMemo(() => {
    const dims = dimsFromSearch();
    const env = getExpenseLoadEnv(tenantCode);
    const balanceRows = loadExpenseBalanceRows(tenantCode, dims.fiscalYear);
    const oblRows = (obligationOptions || []).map((o) => ({
      ...o,
      id: o.obligationUid || o.value,
    }));
    const upstreamTab = {
      id: 'obligation',
      label: '원인행위',
      hint: '확정·승인된 원인행위를 불러옵니다. 없으면 현액 탭에서 직접 등록할 수 있습니다.',
      columns: [
        hiCol.roTextColumn({ header: '번호', field: 'obligationNo', size: 80, align: 'center' }),
        hiCol.roTextColumn({ header: '부서', field: 'deptName', size: 100 }),
        hiCol.roTextColumn({ header: '사업', field: 'projectName', size: 110 }),
        hiCol.roTextColumn({ header: '과목', field: 'subjectName', size: 110 }),
        hiCol.roNumberColumn({ header: '금액', field: 'obligationAmt', size: 100 }),
        hiCol.roTextColumn({ header: '상태', field: 'status', size: 80, align: 'center' }),
      ],
      rows: oblRows,
    };
    const built = buildLoadDialogTabs({
      tabsMode: env.resolutionTabs,
      defaultTab: env.resolutionDefaultTab,
      upstreamTab,
      balanceRows,
      bothTitle: '결의 불러오기',
      balanceOnlyTitle: '현액 조회 (지출)',
    });
    return { ...built, enableSearch: true, searchConfig: BALANCE_SEARCH_CONFIG };
  }, [dimsFromSearch, tenantCode, obligationOptions]);

  const handleLoadSelect = useCallback(
    async (row) => {
      const r = Array.isArray(row) ? row[0] : row;
      if (!r) return false;
      const tab = r._lookupTab || '';
      const isObligation =
        tab === 'obligation' ||
        (!tab && r.obligationUid && (r.obligationNo || r.value));

      if (isObligation) {
        applyObligationToForm(r);
        return true;
      }

      const dims = dimsFromSearch();
      const ok = await confirmUpstreamDupIfNeeded({
        tenantCode,
        dims,
        balanceRow: r,
        upstreamKind: 'obligation',
        showConfirm,
        showAlert,
      });
      if (!ok) return false;
      applyBalanceFields(setValue, r);
      setValue('obligationUid', '');
      setValue('obligationNo', '');
      setValue('obligationPicker', '');
      return true;
    },
    [applyObligationToForm, dimsFromSearch, tenantCode, setValue]
  );

  const searchLookupConfig = useMemo(() => {
    const fy = String(searchFiscalYearWatch || getCurrentFiscalYear()).trim();
    if (searchLookup.type === 'dept') {
      const rows = filterDepts(loadDepts(tenantCode), { useYn: 'Y' });
      return {
        title: '부서 조회 (검색)',
        rows: rows.map((r) => ({ ...r, id: r.deptUid || r.deptCode })),
        columns: [
          hiCol.roTextColumn({ header: '부서코드', field: 'deptCode', size: 100 }),
          hiCol.roTextColumn({ header: '부서명', field: 'deptName', size: 160 }),
        ],
        enableSearch: true,
        searchConfig: { mode: 'unified', keywordFields: ['deptCode', 'deptName'] },
      };
    }
    if (searchLookup.type === 'project') {
      const loaded = loadOrSeedProjects({ tenantCode, fiscalYear: fy }) || [];
      const rows = (Array.isArray(loaded) ? loaded : []).filter((r) => r.useYn !== 'N');
      return {
        title: '사업 조회 (검색)',
        rows: rows.map((r) => ({ ...r, id: r.projectUid || r.projectCode })),
        columns: [
          hiCol.roTextColumn({ header: '사업코드', field: 'projectCode', size: 110 }),
          hiCol.roTextColumn({ header: '사업명', field: 'projectName', size: 180 }),
        ],
        enableSearch: true,
        searchConfig: { mode: 'unified', keywordFields: ['projectCode', 'projectName'] },
      };
    }
    if (searchLookup.type === 'subject') {
      const rows = (loadSubjectsByFilter({ tenantCode, fiscalYear: fy, inoutDiv: '지출' }) || []).filter(
        (r) => r.useYn !== 'N' && r.mod !== 'D'
      );
      return {
        title: '과목 조회 (검색)',
        rows: rows.map((r) => ({
          ...r,
          id: r.subjectUid || r.subjectCode,
        })),
        columns: [
          hiCol.roTextColumn({ header: '과목코드', field: 'subjectDispCode', size: 110 }),
          hiCol.roTextColumn({ header: '과목명', field: 'subjectName', size: 160 }),
        ],
        enableSearch: true,
        searchConfig: {
          mode: 'unified',
          keywordFields: ['subjectDispCode', 'subjectCode', 'subjectName'],
        },
      };
    }
    return { title: '', rows: [], columns: [], enableSearch: false };
  }, [searchLookup.type, tenantCode, searchFiscalYearWatch]);

  const applySearchLookup = useCallback(
    (payload) => {
      const rows = (Array.isArray(payload) ? payload : [payload]).filter(Boolean);
      const axis = {
        dept: {
          keys: ['searchDeptCode', 'searchDeptName', 'searchDeptDisp'],
          code: (r) => r.deptCode,
          name: (r) => r.deptName,
        },
        project: {
          keys: ['searchProjectCode', 'searchProjectName', 'searchProjectDisp'],
          code: (r) => r.projectCode,
          name: (r) => r.projectName,
        },
        subject: {
          keys: ['searchSubjectCode', 'searchSubjectName', 'searchSubjectDisp'],
          code: (r) => r.subjectDispCode || r.subjectCode,
          name: (r) => r.subjectName,
        },
      }[searchLookup.type];
      if (!axis) return true;

      const [codeKey, nameKey, dispKey] = axis.keys;
      if (!rows.length) {
        setValue(codeKey, '');
        setValue(nameKey, '');
        setValue(dispKey, '');
        return true;
      }

      const codes = rows.map((r) => String(axis.code(r) || '').trim()).filter(Boolean);
      const names = rows.map((r) => String(axis.name(r) || '').trim()).filter(Boolean);
      setValue(codeKey, codes.join(','));
      setValue(nameKey, names.join(','));
      setValue(dispKey, summarizeSelection(names));
      return true;
    },
    [searchLookup.type, setValue]
  );

  const searchFields = useMemo(
    () => [
      [
        {
          name: 'searchFiscalYear',
          label: '회계연도',
          type: 'fiscalYear',
          parentWidth: SEARCH_SLOT_W[0],
          width: '88px',
          labelWidth: SEARCH_LABEL_W,
        },
        {
          name: 'searchStatus',
          label: '상태',
          type: 'select',
          options: [{ value: '', label: '전체' }, ...EXPENSE_STATUS_OPTIONS],
          parentWidth: SEARCH_SLOT_W[1],
          width: '140px',
          labelWidth: SEARCH_LABEL_W,
        },
        {
          name: 'searchResolutionDateRange',
          label: '결의일',
          type: 'dateRange',
          startFieldName: 'searchDateFrom',
          endFieldName: 'searchDateTo',
          parentWidth: SEARCH_SLOT_W[2],
          width: SEARCH_DATE_W,
          labelWidth: SEARCH_LABEL_W,
          fiscalYear: searchFiscalYearWatch || getCurrentFiscalYear(),
          presetSet: 'inquiry',
        },
      ],
      [
        {
          name: 'searchDeptDisp',
          label: '부서',
          type: 'text',
          parentWidth: SEARCH_SLOT_W[0],
          width: '160px',
          labelWidth: SEARCH_LABEL_W,
          readonly: true,
          showSearch: true,
          searchTitle: '부서 조회',
          placeholder: '전체',
          onSearch: () => setSearchLookup({ open: true, type: 'dept' }),
          title: selectionTooltip(searchDeptNameWatch, '팝업에서 선택 · [조건 해제]로 전체'),
        },
        {
          name: 'searchProjectDisp',
          label: '사업',
          type: 'text',
          parentWidth: SEARCH_SLOT_W[1],
          width: '210px',
          labelWidth: SEARCH_LABEL_W,
          readonly: true,
          showSearch: true,
          searchTitle: '사업 조회',
          placeholder: '전체',
          onSearch: () => setSearchLookup({ open: true, type: 'project' }),
          title: selectionTooltip(searchProjectNameWatch, '팝업에서 선택 · [조건 해제]로 전체'),
        },
        {
          name: 'searchSubjectDisp',
          label: '과목',
          type: 'text',
          parentWidth: SEARCH_SLOT_W[2],
          width: '210px',
          labelWidth: SEARCH_LABEL_W,
          readonly: true,
          showSearch: true,
          searchTitle: '과목 조회',
          placeholder: '전체',
          onSearch: () => setSearchLookup({ open: true, type: 'subject' }),
          title: selectionTooltip(searchSubjectNameWatch, '팝업에서 선택 · [조건 해제]로 전체'),
        },
        {
          name: 'searchRemark',
          label: '적요',
          type: 'text',
          width: '320px',
          labelWidth: SEARCH_LABEL_W,
          placeholder: '부분 일치',
        },
      ],
    ],
    [searchFiscalYearWatch, searchDeptNameWatch, searchProjectNameWatch, searchSubjectNameWatch]
  );

  const masterFields = useMemo(
    () => [
      [
        {
          name: 'resolutionNo',
          label: '결의번호',
          type: 'text',
          readOnly: true,
          parentWidth: MASTER_SLOT_W[0],
          width: '112px',
          labelWidth: MASTER_LABEL_W,
        },
        {
          name: 'deptName',
          label: '부서',
          type: 'text',
          readOnly: true,
          parentWidth: MASTER_SLOT_W[1],
          width: '150px',
          labelWidth: MASTER_LABEL_W,
        },
        {
          name: 'projectName',
          label: '사업',
          type: 'text',
          readOnly: true,
          parentWidth: MASTER_SLOT_W[2],
          width: '210px',
          labelWidth: MASTER_LABEL_W,
        },
        {
          name: 'subjectName',
          label: '과목',
          type: 'text',
          readOnly: true,
          parentWidth: MASTER_SLOT_W[3],
          width: '210px',
          labelWidth: MASTER_LABEL_W,
        },
        {
          name: 'fundName',
          label: '재원',
          type: 'text',
          readOnly: true,
          parentWidth: MASTER_SLOT_W[4],
          width: '120px',
          labelWidth: MASTER_LABEL_W,
        },
        {
          name: 'obligationNo',
          label: '원인행위',
          type: 'text',
          readOnly: true,
          parentWidth: MASTER_SLOT_W[5],
          width: '112px',
          labelWidth: MASTER_LABEL_W,
        },
      ],
      [
        {
          name: 'resolutionAmt',
          label: '결의금액',
          type: 'number',
          parentWidth: MASTER_SLOT_W[0],
          width: '130px',
          labelWidth: MASTER_LABEL_W,
          readOnly: masterReadonly,
        },
        {
          name: 'resolutionDate',
          label: '결의일',
          type: 'date',
          parentWidth: MASTER_SLOT_W[1],
          width: '140px',
          labelWidth: MASTER_LABEL_W,
          readOnly: masterReadonly,
        },
        {
          name: 'resolutionType',
          label: '유형',
          type: 'select',
          options: RESOLUTION_TYPE_OPTIONS,
          parentWidth: MASTER_SLOT_W[2],
          width: '150px',
          labelWidth: MASTER_LABEL_W,
          readOnly: masterReadonly,
        },
        {
          name: 'statusNm',
          label: '상태',
          type: 'text',
          readOnly: true,
          parentWidth: MASTER_SLOT_W[3],
          width: '90px',
          labelWidth: MASTER_LABEL_W,
        },
        {
          name: 'remark',
          label: '적요',
          type: 'text',
          parentWidth: MASTER_SLOT_W_REMARK,
          width: '330px',
          labelWidth: MASTER_LABEL_W,
          readOnly: masterReadonly,
        },
      ],
    ],
    [masterReadonly]
  );

  // ─── 엑셀 (결의목록 그리드는 미등록 · 마스터 폼 + 디테일만) ───
  const getMasterExportData = useCallback(() => {
    const v = getValues();
    const uid = String(v.resolutionUid || selectedUidRef.current || '').trim();
    if (!uid) return [];
    return [
      {
        resolutionUid: uid,
        resolutionNo: v.resolutionNo || '',
        obligationNo: v.obligationNo || '',
        fiscalYear: v.fiscalYear || '',
        deptCode: v.deptCode || '',
        deptName: v.deptName || '',
        projectCode: v.projectCode || '',
        projectName: v.projectName || '',
        subjectCode: v.subjectCode || '',
        subjectName: v.subjectName || '',
        fundCode: v.fundCode || '',
        fundName: v.fundName || '',
        resolutionAmt: Number(v.resolutionAmt) || 0,
        resolutionDate: v.resolutionDate || '',
        resolutionType: v.resolutionType || '',
        resolutionTypeNm: resolutionTypeLabel(v.resolutionType),
        status: v.status || '',
        statusNm: expenseStatusLabel(v.status) || v.statusNm || '',
        remark: v.remark || '',
      },
    ];
  }, [getValues]);

  const getMasterExportCols = useCallback(
    () => [
      { header: '결의번호', accessorKey: 'resolutionNo' },
      { header: '원인행위', accessorKey: 'obligationNo' },
      { header: '회계연도', accessorKey: 'fiscalYear' },
      { header: '부서코드', accessorKey: 'deptCode' },
      { header: '부서', accessorKey: 'deptName' },
      { header: '사업코드', accessorKey: 'projectCode' },
      { header: '사업', accessorKey: 'projectName' },
      { header: '과목코드', accessorKey: 'subjectCode' },
      { header: '과목', accessorKey: 'subjectName' },
      { header: '재원코드', accessorKey: 'fundCode' },
      { header: '재원', accessorKey: 'fundName' },
      { header: '결의금액', accessorKey: 'resolutionAmt' },
      { header: '결의일', accessorKey: 'resolutionDate' },
      { header: '유형', accessorKey: 'resolutionTypeNm' },
      { header: '상태', accessorKey: 'statusNm' },
      { header: '적요', accessorKey: 'remark' },
    ],
    []
  );

  const getDetailExportData = useCallback(
    () =>
      (detailRef.current?.getFilteredData?.() ?? detailRef.current?.getData?.() ?? []).filter(
        (r) => r.mod !== 'D'
      ),
    []
  );
  const getDetailExportCols = useCallback(
    () => detailRef.current?.getDataColumns?.() ?? detailColumns,
    [detailColumns]
  );

  useRegisterGridExport({
    id: MASTER_EXPORT_ID,
    label: '결의마스터',
    getData: getMasterExportData,
    getDataColumns: getMasterExportCols,
    joinKeys: ['resolutionUid'],
    order: 0,
  });
  useRegisterGridExport({
    id: DETAIL_GRID_ID,
    label: '결의디테일',
    getData: getDetailExportData,
    getDataColumns: getDetailExportCols,
    joinKeys: ['resolutionUid'],
    order: 1,
  });

  const { openDialog: openExcelDialog, dialog: excelDialog } = useExcelExportAction({
    fileName: isApprove ? '지출결의승인' : '지출결의등록',
  });

  const actionButtons = useMemo(() => {
    const base = [{ id: 'search', label: '조회', icon: '🔍', onClick: handleSearch, hotkey: 'F7' }];
    if (isApprove) return base;
    return [
      ...base,
      { id: 'new', label: '추가', icon: '➕', onClick: handleNew, hotkey: 'F8' },
      {
        id: 'delete',
        label: '삭제',
        icon: '❌',
        onClick: handleActiveDelete,
        hotkey: 'F9',
        variant: 'danger',
        title: activeLabel ? `삭제 → ${activeLabel}` : '삭제 (F9)',
      },
      { id: 'save', label: '저장', icon: '✅', onClick: handleSave, hotkey: 'F10', variant: 'success' },
      {
        id: 'excel',
        label: '엑셀',
        icon: '📥',
        onClick: openExcelDialog,
        hotkey: 'F11',
        title: '엑셀 다운로드 (F11) · 결의목록 제외',
      },
    ];
  }, [isApprove, handleSearch, handleNew, handleActiveDelete, handleSave, openExcelDialog, activeLabel]);

  const extraButtons = useMemo(() => {
    if (isApprove) {
      return [
        { id: 'approve', label: '승인', onClick: handleApprove, variant: 'primary' },
        { id: 'reject', label: '반려', onClick: handleReject, variant: 'danger' },
      ];
    }
    return [{ id: 'request', label: '승인요청', onClick: handleRequest }];
  }, [isApprove, handleApprove, handleReject, handleRequest]);

  return (
    <PageContainer>
      <FormProvider {...methods}>
        <ActionButtonGroup buttons={actionButtons} extraButtons={extraButtons} />
        <Stack {...scrollbarHoverProps}>
          <CollapsibleSearchPanel
            storageKey={`expense-resolution-${screenMode}`}
            prefsUserId={prefsUserId}
            title="조회조건"
          >
            <FormArea compact fields={searchFields} />
          </CollapsibleSearchPanel>

          <Hint>
            {isApprove
              ? '승인요청 결의(마스터+디테일) 선택 후 승인/반려'
              : '불러오기 · 전자증빙(점유·저장 싱크) · 목록 활성 시 F9=결의삭제(증빙 미결 복귀) · 엑셀(F11)은 결의목록 제외'}
            {activeLabel ? ` · 활성: ${activeLabel}` : ''}
          </Hint>

          {isApprove ? (
            <Panel $flex="1 0 auto" $height={LIST_PANEL_H}>
              <ActiveGridHeader gridId={APPROVE_GRID_ID}>결의 승인 (조인)</ActiveGridHeader>
              <GridBody>
                <HiGridComponent
                  ref={approveRef}
                  columns={approveColumns}
                  showRowNumber
                  rowNumberSize={48}
                  enableColumnFilters
                  enableHeaderMenu
                  enableQuickExport
                  exportFileName="지출결의승인"
                  gridId={`expense-resolution-approve-${screenMode}`}
                  prefsUserId={prefsUserId}
                />
              </GridBody>
            </Panel>
          ) : (
            <>
              <Panel>
                <GridExportFocusScope gridId={MASTER_EXPORT_ID} style={{ flex: '0 0 auto' }}>
                  <MasterToolbar>
                    {/* 제목(영역 식별) → 불러오기류 버튼 · 승인요청 등은 ActionButtonGroup 우측 */}
                    <MasterToolbarTitle>결의 마스터</MasterToolbarTitle>
                    <MasterToolbarActions>
                      <LoadBtn
                        type="button"
                        onClick={() => {
                          loadObligationPicker();
                          setLoadLookupOpen(true);
                        }}
                        disabled={masterReadonly}
                        title={
                          masterReadonly
                            ? '작성중·반려 건만 불러올 수 있습니다. [추가]로 신규 작성하세요.'
                            : '원인행위·현액 불러오기'
                        }
                      >
                        불러오기 ···
                      </LoadBtn>
                      <LoadBtn
                        type="button"
                        onClick={openEvidenceLookup}
                        disabled={masterReadonly}
                        title={
                          masterReadonly
                            ? '작성중·반려 건만 전자증빙을 불러올 수 있습니다.'
                            : '미결 전자증빙(법인카드·세금계산서) → 결의 초안(마스터+디테일)'
                        }
                      >
                        전자증빙 ···
                      </LoadBtn>
                    </MasterToolbarActions>
                  </MasterToolbar>
                  <FormArea dense fields={masterFields} />
                </GridExportFocusScope>
              </Panel>

              {/* 남는 공간은 차지하되(grow), 목록을 펼쳐도 줄어들지 않음(shrink 0) */}
              <Panel $flex="1 0 auto" $height={DETAIL_PANEL_H}>
                <GridExportFocusScope gridId={DETAIL_GRID_ID}>
                  <ActiveGridScope
                    gridId={DETAIL_GRID_ID}
                    label="결의디테일"
                    order={20}
                    onAdd={dispatchDetailAdd}
                    onDelete={async () => detailGrid.handleDelete?.()}
                    canAdd={!masterReadonly}
                    canDelete={!masterReadonly}
                  >
                    <ActiveGridHeader gridId={DETAIL_GRID_ID}>결의 디테일</ActiveGridHeader>
                    <GridBody>
                      <HiGridComponent
                        ref={detailRef}
                        columns={detailColumns}
                        showRowNumber
                        rowNumberSize={48}
                        enableColumnFilters
                        enableContextMenu={!masterReadonly}
                        enableRowEditMenu={!masterReadonly}
                        enableCut={!masterReadonly}
                        enableFindReplace
                        allowReplace={!masterReadonly}
                        enableHeaderMenu
                        enableSelectionStats
                        gridId={`expense-resolution-detail-${screenMode}`}
                        prefsUserId={prefsUserId}
                        headerActive={activeGridId === DETAIL_GRID_ID}
                        {...(masterReadonly ? {} : detailGrid.contextMenuProps || {})}
                      />
                    </GridBody>
                  </ActiveGridScope>
                </GridExportFocusScope>
              </Panel>

              <CollapsibleSectionPanel
                storageKey={`expense-resolution-list-${screenMode}`}
                prefsUserId={prefsUserId}
                title="결의 목록"
                summary={listCount ? `${listCount}건` : '조회 후 표시'}
                defaultCollapsed
                expandedHeight={LIST_PANEL_H}
                expandedMaxHeight="none"
              >
                <ActiveGridScope
                  gridId={LIST_GRID_ID}
                  label="결의목록"
                  order={30}
                  onAdd={handleNew}
                  onDelete={handleDeleteResolution}
                  canAdd
                  canDelete
                >
                  <GridBody>
                    <HiGridComponent
                      ref={listRef}
                      columns={listColumns}
                      showRowNumber
                      rowNumberSize={48}
                      enableColumnFilters
                      enableHeaderMenu
                      gridId={`expense-resolution-list-${screenMode}`}
                      prefsUserId={prefsUserId}
                      headerActive={activeGridId === LIST_GRID_ID}
                    />
                  </GridBody>
                </ActiveGridScope>
              </CollapsibleSectionPanel>
            </>
          )}
        </Stack>
      </FormProvider>

      {!isApprove && (
        <CodeLookupDialog
          open={loadLookupOpen}
          title={loadDialogConfig.title}
          tabs={loadDialogConfig.tabs}
          columns={loadDialogConfig.columns}
          rows={loadDialogConfig.rows}
          defaultTabId={loadDialogConfig.defaultTabId}
          enableSearch={loadDialogConfig.enableSearch}
          searchConfig={loadDialogConfig.searchConfig}
          prefsUserId={prefsUserId}
          sizeKey="codeLookup-expense-resolution-load"
          dialogWidth="860px"
          onSelect={handleLoadSelect}
          onClose={() => setLoadLookupOpen(false)}
        />
      )}

      {!isApprove && (
        <CodeLookupDialog
          open={cardLookupOpen}
          title={evidenceLookupConfig.title}
          tabs={evidenceLookupConfig.tabs}
          defaultTabId={evidenceLookupConfig.defaultTabId}
          multiSelect
          enableSearch={evidenceLookupConfig.enableSearch}
          searchConfig={evidenceLookupConfig.searchConfig}
          prefsUserId={prefsUserId}
          sizeKey="codeLookup-expense-resolution-evidence"
          dialogWidth="920px"
          onSelect={handleEvidenceSelect}
          onClose={() => setCardLookupOpen(false)}
        />
      )}

      {!isApprove && (
        <CodeLookupDialog
          open={axisPickOpen}
          title={axisPickConfig.title}
          columns={axisPickConfig.columns}
          rows={axisPickConfig.rows}
          enableSearch={axisPickConfig.enableSearch}
          searchConfig={axisPickConfig.searchConfig}
          prefsUserId={prefsUserId}
          sizeKey="codeLookup-expense-resolution-evidence-axis"
          dialogWidth="860px"
          onSelect={handleAxisPickSelect}
          onClose={() => {
            setAxisPickOpen(false);
            pendingCardEvidencesRef.current = [];
          }}
        />
      )}

      <CodeLookupDialog
        open={searchLookup.open}
        title={searchLookupConfig.title}
        columns={searchLookupConfig.columns}
        rows={searchLookupConfig.rows}
        enableSearch={searchLookupConfig.enableSearch}
        searchConfig={searchLookupConfig.searchConfig}
        multiSelect
        prefsUserId={prefsUserId}
        sizeKey={`codeLookup-resolution-search-${searchLookup.type || 'x'}`}
        onSelect={applySearchLookup}
        onClose={() => setSearchLookup({ open: false, type: '' })}
      />

      {!isApprove && excelDialog}
    </PageContainer>
  );
}

export default function ExpenseResolutionWorkspace(props) {
  return (
    <GridExportProvider>
      <ActiveGridProvider>
        <ExpenseResolutionWorkspaceInner {...props} />
      </ActiveGridProvider>
    </GridExportProvider>
  );
}
